/**
 * Discount Rules Engine — Admin-configurable ladder discounts.
 * Rules auto-apply to estimates / BOQ worksheets based on main-category mix.
 * Each rule can carry an optional valid_till date → PDF stamps "Limited-time offer" badge.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tag, Plus, Edit3, Trash2, Clock, CheckCircle, XCircle, X } from 'lucide-react';
import { API } from '@/App';

const DEFAULT_RULE = {
  name: '', description: '',
  required_categories: [], min_category_count: null,
  discount_pct: 7, valid_till: '', active: true, priority: 10, show_expiry_stamp: true,
};

export default function DiscountRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // { ...DEFAULT_RULE } or existing
  const [categoryOptions, setCategoryOptions] = useState([]);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    setLoading(true);
    try {
      const [rulesRes, catsRes] = await Promise.all([
        axios.get(`${API}/discount-rules`, { headers }),
        axios.get(`${API}/boq-categories/main`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRules(rulesRes.data || []);
      const list = Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data?.items || []);
      setCategoryOptions(list.filter(c => c.code).map(c => ({ code: c.code.toUpperCase(), name: c.name })));
    } catch (e) {
      toast.error('Failed to load discount rules');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...DEFAULT_RULE }); setEditOpen(true); };
  const openEdit = (r) => { setEditing({ ...r, valid_till: r.valid_till ? r.valid_till.slice(0, 10) : '' }); setEditOpen(true); };

  const saveRule = async () => {
    if (!editing?.name?.trim()) { toast.error('Rule name is required'); return; }
    const body = {
      ...editing,
      required_categories: (editing.required_categories || []).map(c => c.toUpperCase()),
      min_category_count: editing.min_category_count ? +editing.min_category_count : null,
      discount_pct: +editing.discount_pct || 0,
      priority: +editing.priority || 0,
      valid_till: editing.valid_till || null,
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/discount-rules/${editing.id}`, body, { headers });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/discount-rules`, body, { headers });
        toast.success('Rule created');
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API}/discount-rules/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleCat = (code) => {
    const list = editing.required_categories || [];
    const next = list.includes(code) ? list.filter(c => c !== code) : [...list, code];
    setEditing({ ...editing, required_categories: next });
  };

  const isExpired = (r) => {
    if (!r.valid_till) return false;
    try { return new Date(r.valid_till) < new Date(new Date().toDateString()); } catch { return false; }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="discount-rules-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><Tag size={22} className="text-emerald-600" />Discount Rules Engine</h1>
          <p className="text-sm text-stone-500 mt-0.5">Ladder discounts auto-applied to estimates &amp; BOQ worksheets based on main-category mix. Optional expiry stamps PDFs with a conversion-boosting "Limited-time offer" badge.</p>
        </div>
        <Button onClick={openNew} data-testid="add-discount-rule-btn"><Plus size={15} className="mr-1" />Add Rule</Button>
      </div>

      <Card>
        <CardContent className="pt-5 pb-3 px-0">
          {loading ? (
            <p className="text-center text-sm text-stone-500 py-8">Loading...</p>
          ) : rules.length === 0 ? (
            <p className="text-center text-sm text-stone-500 py-8">No rules yet — add your first rule.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b-2 border-stone-200">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">Trigger</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-stone-600 uppercase">Discount</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-stone-600 uppercase">Priority</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-stone-600 uppercase">Valid Till</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-stone-600 uppercase">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.id} className="border-b hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-stone-800 text-sm">{r.name}</p>
                      {r.description && <p className="text-xs text-stone-500 mt-0.5">{r.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">
                      {(r.required_categories || []).length > 0 && (
                        <span>Categories: <b>{r.required_categories.join(' + ')}</b></span>
                      )}
                      {r.min_category_count && (
                        <span>{r.required_categories?.length ? ' · ' : ''}Min ≥ {r.min_category_count} categories</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700">{r.discount_pct}%</td>
                    <td className="px-4 py-3 text-center text-stone-600">{r.priority || 0}</td>
                    <td className="px-4 py-3 text-xs text-stone-700">
                      {r.valid_till ? (
                        <span className={`inline-flex items-center gap-1 ${isExpired(r) ? 'text-red-600' : 'text-amber-700'}`}>
                          <Clock size={11} />{new Date(r.valid_till).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {isExpired(r) && <span className="ml-1 text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">EXPIRED</span>}
                        </span>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.active ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={12} />Active</span> : <span className="inline-flex items-center gap-1 text-xs text-stone-500"><XCircle size={12} />Off</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-rule-${r.id}`}><Edit3 size={13} /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(r.id)} data-testid={`delete-rule-${r.id}`}><Trash2 size={13} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEditOpen(false)} data-testid="edit-rule-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2"><Tag size={18} className="text-emerald-600" />{editing.id ? 'Edit' : 'New'} Discount Rule</h3>
              <button onClick={() => setEditOpen(false)} className="text-stone-400 hover:text-stone-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Rule Name *</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Kitchen + Wardrobe Bundle" data-testid="rule-name-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Input value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="Short client-friendly description" />
                </div>
                <div>
                  <Label>Discount %</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={editing.discount_pct} onChange={e => setEditing({ ...editing, discount_pct: +e.target.value })} data-testid="rule-pct-input" />
                </div>
                <div>
                  <Label>Priority (higher wins)</Label>
                  <Input type="number" value={editing.priority || 0} onChange={e => setEditing({ ...editing, priority: +e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Required Category Codes (all must be present)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {categoryOptions.map(c => {
                    const checked = (editing.required_categories || []).includes(c.code);
                    return (
                      <button
                        type="button"
                        key={c.code}
                        onClick={() => toggleCat(c.code)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${checked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}
                        data-testid={`rule-cat-${c.code}`}
                        title={c.name}
                      >
                        {c.code}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>OR: Match on Min Distinct Category Count</Label>
                  <Input type="number" min="0" value={editing.min_category_count ?? ''} onChange={e => setEditing({ ...editing, min_category_count: e.target.value ? +e.target.value : null })} placeholder="e.g. 5 for full-home" />
                </div>
                <div>
                  <Label>Valid Till (optional)</Label>
                  <Input type="date" value={editing.valid_till || ''} onChange={e => setEditing({ ...editing, valid_till: e.target.value })} data-testid="rule-valid-till-input" />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 border-t">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="rule-active-toggle" /> Active</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.show_expiry_stamp} onChange={e => setEditing({ ...editing, show_expiry_stamp: e.target.checked })} /> Stamp "Limited-time offer" on PDF when rule has an expiry</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-stone-50">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={saveRule} data-testid="save-rule-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">{editing.id ? 'Update' : 'Create'} Rule</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
